/**
 * Send community connection invites by email or SMS (accept path already exists).
 * When `channel` is set by the parent (invite sheet), the email/SMS switch is hidden.
 */

import React, { useEffect, useState } from 'react';
import { Button, Input, Radio, Space, Typography, message } from 'antd';
import { tripShareEn as t } from '../../content/tripShare.en';
import {
  InviteAcceptError,
  createConnectionInviteByEmail,
  createConnectionInviteByPhone,
} from '../../services/inviteService';

export type ConnectionInviteChannel = 'email' | 'sms';

type Props = {
  tripId?: string | null;
  role?: string | null;
  /** When set, locks the form to this channel and hides the email/SMS switch. */
  channel?: ConnectionInviteChannel;
  onSent?: () => void;
  hideHeading?: boolean;
  /** Return false to abort send (e.g. community gate cancelled). */
  onBeforeSend?: () => boolean | Promise<boolean>;
};

const ConnectionInviteForm: React.FC<Props> = ({
  tripId,
  role = 'viewer',
  channel: channelProp,
  onSent,
  hideHeading = false,
  onBeforeSend,
}) => {
  const [channel, setChannel] = useState<ConnectionInviteChannel>(channelProp ?? 'email');
  const [value, setValue] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [lastLink, setLastLink] = useState<string | null>(null);

  useEffect(() => {
    if (channelProp) {
      setChannel(channelProp);
      setValue('');
    }
  }, [channelProp]);

  const handleSend = async (): Promise<void> => {
    if (onBeforeSend) {
      const ok = await onBeforeSend();
      if (!ok) return;
    }
    setSending(true);
    setLastLink(null);
    try {
      const created =
        channel === 'email'
          ? await createConnectionInviteByEmail({
              email: value,
              tripId,
              role,
              message: inviteMessage || null,
            })
          : await createConnectionInviteByPhone({
              phone: value,
              tripId,
              role,
              message: inviteMessage || null,
            });
      setLastLink(created.invitationLink);
      message.success(t.connectionInviteSent);
      setValue('');
      onSent?.();
    } catch (e) {
      if (e instanceof InviteAcceptError && e.statusCode === 400) {
        message.error(
          channel === 'email'
            ? t.connectionInviteInvalidEmail
            : t.connectionInviteInvalidPhone
        );
      } else {
        message.error(t.connectionInviteFailed);
      }
    } finally {
      setSending(false);
    }
  };

  const handleCopyLink = async (): Promise<void> => {
    if (!lastLink) return;
    try {
      await navigator.clipboard.writeText(lastLink);
      message.success(t.connectionInviteLinkCopied);
    } catch {
      message.error(t.connectionInviteFailed);
    }
  };

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }} data-testid="connection-invite-form">
      {!hideHeading ? (
        <Typography.Text strong>{t.connectionInviteHeading}</Typography.Text>
      ) : null}
      {!channelProp ? (
        <Radio.Group
          value={channel}
          onChange={(e) => {
            setChannel(e.target.value as ConnectionInviteChannel);
            setValue('');
          }}
        >
          <Radio.Button value="email">{t.connectionInviteEmail}</Radio.Button>
          <Radio.Button value="sms">{t.connectionInvitePhone}</Radio.Button>
        </Radio.Group>
      ) : null}
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={
          channel === 'email'
            ? t.connectionInviteEmailPlaceholder
            : t.connectionInvitePhonePlaceholder
        }
        inputMode={channel === 'email' ? 'email' : 'tel'}
        aria-label={
          channel === 'email'
            ? t.connectionInviteEmailPlaceholder
            : t.connectionInvitePhonePlaceholder
        }
      />
      <Input.TextArea
        value={inviteMessage}
        onChange={(e) => setInviteMessage(e.target.value)}
        placeholder={t.connectionInviteMessage}
        rows={2}
        aria-label={t.connectionInviteMessage}
      />
      <Button
        type="primary"
        loading={sending}
        disabled={!value.trim()}
        onClick={() => void handleSend()}
      >
        {channel === 'email' ? t.connectionInviteSendEmail : t.connectionInviteSendSms}
      </Button>
      {lastLink ? (
        <Button type="link" onClick={() => void handleCopyLink()}>
          {t.connectionInviteCopyLink}
        </Button>
      ) : null}
    </Space>
  );
};

export default ConnectionInviteForm;
